import React from 'react';

import classNames from 'classnames';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import type { DidRouting } from '../api';

interface DidRoutingListProps {
  routes: DidRouting[];
  isLoading?: boolean;
  onEdit: (route: DidRouting) => void;
  onDelete: (route: DidRouting) => void;
}

export const DidRoutingList: React.FC<DidRoutingListProps> = ({
  routes,
  isLoading = false,
  onEdit,
  onDelete,
}) => {
  if (isLoading && routes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-white-1.0 p-8 text-sm text-gray-2.0">
        Cargando rutas DID…
      </div>
    );
  }

  if (routes.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-white-1.0 p-8 text-gray-2.0">
        <Icon name={OrbitIcons.Phone} size={48} className="mb-4 opacity-50" />
        <p className="text-lg">No hay rutas DID configuradas</p>
        <p className="text-sm">Crea una nueva ruta para comenzar</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white-1.0">
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 border-b border-white-2.0 bg-white-1.0">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                DID
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                FreeSWITCH IP
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                Puerto
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                Account ID
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                Carrier
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-gray-3.0">
                Creado
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wide text-gray-3.0">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {routes.map((route) => (
              <tr
                key={route.id}
                className="border-b border-white-2.0 bg-white-0.0 hover:bg-white-1.0"
              >
                <td className="px-4 py-3">
                  <span className="font-mono text-sm font-semibold text-primary-2.0">
                    {route.did}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-sm text-black-1.0">{route.freeswitch_ip}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-black-1.0">{route.freeswitch_port}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-2.0">{route.account_id ?? '-'}</span>
                </td>
                <td className="px-4 py-3">
                  {route.carrier ? (
                    <span
                      className={classNames(
                        'inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-semibold uppercase',
                        'bg-gray-100 text-gray-600'
                      )}
                    >
                      {route.carrier}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-2.0">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-2.0">
                    {route.created_at
                      ? new Date(route.created_at).toLocaleDateString('es-PR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onEdit(route)}
                    className="p-1 text-gray-400 hover:text-primary-2.0 mr-2"
                    title="Editar"
                  >
                    <Icon name={OrbitIcons.Pencil} size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(route)}
                    className="hover:text-red-600 p-1 text-gray-400"
                    title="Eliminar"
                  >
                    <Icon name={OrbitIcons.Trash} size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
